import { getFirestore, doc, setDoc, getDocs, getDoc, collection, deleteDoc, writeBatch } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { CmsProject, CmsTestimonial, CmsInfluencerVideo, CmsPastProject, CmsSiteSettings } from '../types/cms';
import { SeoEngineConfig, SeoPendingApprovalItem, PropertySeoItem, LocalityHubSeoItem, PortalHomepageSeoItem } from '../types/seo';
import { compressImage } from '../utils/mediaOptimizer';

const COLLECTION_NAME = 'website_projects';
const DRAFT_COLLECTION_NAME = 'website_project_drafts';
const SEO_COLLECTION_NAME = 'website_seo';
const SEO_CONFIG_COLLECTION = 'website_seo_config';
const SEO_QUEUE_COLLECTION = 'website_seo_queue';
const LOCALITY_HUBS_COLLECTION = 'website_locality_hubs';
const TESTIMONIALS_COLLECTION = 'website_testimonials';
const INFLUENCER_COLLECTION = 'website_influencer_videos';
const PAST_PROJECTS_COLLECTION = 'website_past_projects';
const SETTINGS_COLLECTION = 'website_settings';

export const DEFAULT_AVSAR_PROJECT: CmsProject = {
  id: 'swarna-avsar',
  slug: 'swarna-avsar',
  name: 'Swarna Avsar',
  tagline: 'Not Only Largest, Finest Too',
  description:
    'Swarna Avsar is a landmark 2 BHK residential development nestled beside Swarna Lagom on Ugat Canal Road, Vanakla, Surat. Spread across three wings — A, B and C — this 14-storey high-rise offers 148 thoughtfully designed homes with premium finishes, modern specifications, and a rich bouquet of campus and terrace amenities. Positioned directly on the 150 ft. canal road with seamless connectivity to Ring Road, Metro, and key landmarks, Swarna Avsar blends the finest location with architectural excellence.',
  shortDescription:
    "148 premium 2 BHK homes across 3 wings on Ugat Canal Road, Vanakla — where Surat's finest address meets exceptional living.",
  location: 'Beside Swarna Lagom, Ugat Canal Road, Vanakla, Surat — 395005',
  city: 'Surat',
  microMarket: 'Vanakla',
  surroundingAreas: ['Jahangirpura', 'Jahangirabad', 'Palanpore Canal Road', 'Dandi Road', 'Ugat Canal Road'],
  status: 'under-construction',
  completionDate: 'December 2026',
  heroImage: '/og-default.jpg',
  thumbnailImage: '/og-default.jpg',
  totalFloors: 14,
  totalUnits: 168,
  reraNumber: 'PR/GJ/SURAT/SURAT CITY/SMC/MAA14060/300824/311228',
  mapEmbedUrl:
    'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3718.816380390014!2d72.7798349!3d21.2391287!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be04d0047257c65%3A0xc813e0f91bb97dac!2sSwarna%20Avsar!5e0!3m2!1sen!2sin!4v1781271895644!5m2!1sen!2sin',
  whatsappNumber: '+919925026000',
  brochureUrl: '',
  configurations: [
    {
      id: 'avsar-cfg-1',
      type: 'Commercial Shops',
      area: '227 – 1,103 sq.ft (RERA Carpet)',
      price: 'Contact for pricing',
      units: 20,
      availabilityStatus: 'Available',
    },
    {
      id: 'avsar-cfg-2',
      type: '2 BHK (Wing A & B)',
      area: '710 – 713 sq.ft (RERA Carpet)',
      price: 'Contact for pricing',
      units: 26,
      availabilityStatus: 'Booking Fast',
    },
    {
      id: 'avsar-cfg-3',
      type: '2 BHK (Wing C)',
      area: '732 – 742 sq.ft (RERA Carpet)',
      price: 'Contact for pricing',
      units: 122,
      availabilityStatus: 'Available',
    },
  ],
  highlights: [
    '3-wing high-rise with 148 premium 2 BHK residences',
    'Prime canal road frontage on 150 Ft. Ugat Canal Road',
    'Exclusive rooftop terrace with open gymnasium & multipurpose court',
    'Generator power backup with point in every flat',
    'Gas line connection & centralized plumbing in each home',
    'Designer entrance foyer with auto elevators in every wing',
    '3-layer parking for residents and visitors',
    'CREDAI member — built on honesty, trust, freedom & growth',
  ],
  amenities: [
    { id: 'av-a1', icon: 'Tree', label: 'Landscaped Garden & Gazebo', category: 'wellness' },
    { id: 'av-a2', icon: 'PlayCircle', label: "Children's Play Area", category: 'wellness' },
    { id: 'av-a3', icon: 'Football', label: 'Multipurpose Sports Court', category: 'wellness' },
    { id: 'av-a4', icon: 'Barbell', label: 'Non-Electric Open Gymnasium', category: 'wellness' },
    { id: 'av-a5', icon: 'ShieldCheck', label: 'Security Cabin & CCTV', category: 'safety' },
    { id: 'av-a6', icon: 'Car', label: '3-Layer Parking', category: 'convenience' },
    { id: 'av-a7', icon: 'Leaf', label: 'Amphitheatre & Fitness Zone', category: 'wellness' },
    { id: 'av-a8', icon: 'Tree', label: 'Jogging Track cum Garden', category: 'wellness' },
  ],
  specifications: [
    {
      id: 'av-s1',
      heading: 'Structure & Flooring',
      items: [
        { label: 'Structure', value: 'Earthquake resistant RCC frame as per IS code' },
        { label: 'Flooring', value: 'Premium vitrified tiles 24×48" or 32×32" in all rooms' },
      ],
    },
    {
      id: 'av-s2',
      heading: 'Kitchen & Wash',
      items: [
        { label: 'Kitchen Platform', value: 'Granite platform with standard quality sink' },
        { label: 'Kitchen Dado', value: 'Decorative granite / glazed tiles up to lintel level' },
        { label: 'Water Purifier', value: 'Dedicated water purifier point provided' },
        { label: 'Wash Area', value: 'Premium kota flooring & glazed dado tiles up to sill level' },
        { label: 'Store Room', value: '3 Marble racks in store room' },
      ],
    },
    {
      id: 'av-s3',
      heading: 'Toilets & Plumbing',
      items: [
        { label: 'Dado', value: 'Decorative granite / glazed tiles up to lintel level' },
        { label: 'Fittings', value: 'Concealed Astral / Ashirwad / Trueflow CPVC-UPVC plumbing' },
        { label: 'Sanitary', value: 'Wall-hung water closet in both bathrooms' },
        { label: 'Hot Water', value: 'Centralised hot water supply point in both bathrooms' },
      ],
    },
    {
      id: 'av-s4',
      heading: 'Doors, Windows & Balcony',
      items: [
        { label: 'Main Door', value: 'Decorative door with granite or wooden frame' },
        { label: 'Internal Doors', value: 'Laminated flush doors with standard locks' },
        { label: 'Windows', value: 'Anodized sliding aluminium sections with granite seal' },
        { label: 'Living Balcony', value: 'Glass railing with premium flooring' },
        { label: 'Other Balcony', value: 'M.S. railing with premium flooring' },
      ],
    },
    {
      id: 'av-s5',
      heading: 'Electrical & AC',
      items: [
        { label: 'Wiring', value: 'Concealed Polycab / RR wiring with modular switches' },
        { label: 'AC Point', value: 'Provided in master bedroom' },
        { label: 'Power Backup', value: 'Generator backup with point in every flat' },
        { label: 'Gas Line', value: 'Gujarat gas line connection provided' },
      ],
    },
    {
      id: 'av-s6',
      heading: 'Elevators & Security',
      items: [
        { label: 'Elevators', value: '2 auto collective elevators per wing' },
        { label: 'Security', value: 'Multi-point CCTV surveillance system in common areas' },
        { label: 'Foyer Display', value: 'Television display in each building foyer' },
        { label: 'Paint', value: 'Birla / Durawhite or equivalent putty finish on internal walls' },
      ],
    },
  ],
  gallery: [
    { id: 'av-g1', src: '/og-default.jpg', alt: 'Bird Eye View', caption: 'Bird Eye View', category: 'renders' },
    { id: 'av-g2', src: '/og-default.jpg', alt: 'Canal View Elevation', caption: 'Canal View Elevation', category: 'renders' },
    { id: 'av-g3', src: '/og-default.jpg', alt: 'Rear View', caption: 'Rear View', category: 'renders' },
    { id: 'av-g4', src: '/og-default.jpg', alt: 'Deck Balconies', caption: 'Deck Balconies', category: 'renders' },
    { id: 'av-g5', src: '/og-default.jpg', alt: 'Left Side Architectural View', caption: 'Left Side View', category: 'renders' },
    { id: 'av-g6', src: '/og-default.jpg', alt: 'Right Side View', caption: 'Right Side View', category: 'renders' },
    { id: 'av-g7', src: '/og-default.jpg', alt: 'Night View', caption: 'Night View from Canal Road', category: 'renders' },
    { id: 'av-g8', src: '/og-default.jpg', alt: 'Lobby Area', caption: 'Designer Entrance Lobby', category: 'renders' },
    { id: 'av-g9', src: '/og-default.jpg', alt: 'Living Room', caption: 'Living & Dining Room', category: 'renders' },
    { id: 'av-g10', src: '/og-default.jpg', alt: 'Kitchen', caption: 'Kitchen with Granite Platform', category: 'renders' },
    { id: 'av-g11', src: '/og-default.jpg', alt: 'Master Bedroom', caption: 'Master Bedroom Layout', category: 'renders' },
    { id: 'av-g12', src: '/og-default.jpg', alt: 'Ground Floor Plan', caption: 'Ground Floor Commercial & Parking Plan', category: 'floor-plans' },
    { id: 'av-g13', src: '/og-default.jpg', alt: 'First Floor Plan', caption: 'First Floor Residential Layout', category: 'floor-plans' },
    { id: 'av-g14', src: '/og-default.jpg', alt: 'Typical Floor Plan', caption: 'Typical 2 BHK 3-Wing Floor Plan', category: 'floor-plans' },
  ],
  documents: [
    { id: 'av-d1', label: 'RERA Certificate', description: 'Official GujRERA Registration Document', filename: 'swarna-avsar-rera.pdf', fileUrl: '' },
    { id: 'av-d2', label: 'NA Order', description: 'Non-Agricultural Land Order', filename: 'swarna-avsar-na-order.pdf', fileUrl: '' },
    { id: 'av-d3', label: 'Commencement Certificate', description: 'SMC Approved Building Sanction', filename: 'swarna-avsar-commencement.pdf', fileUrl: '' },
    { id: 'av-d4', label: 'Airport NOC', description: 'Civil Aviation Authority Approval', filename: 'swarna-avsar-airport-noc.pdf', fileUrl: '' },
    { id: 'av-d5', label: 'Fire NOC', description: 'Surat Fire Department NOC', filename: 'swarna-avsar-fire-noc.pdf', fileUrl: '' },
  ],
  faqs: [
    {
      id: 'av-f1',
      question: 'What is Swarna Avsar?',
      answer: 'Swarna Avsar is a premium 2 BHK residential project by Swarna Group, located on Ugat Canal Road, Vanakla, Surat. It features 148 homes across 3 wings, offering modern living with quality construction and thoughtful design.',
      order: 1,
    },
    {
      id: 'av-f2',
      question: 'Where is Swarna Avsar located?',
      answer: 'Swarna Avsar is located on Ugat Canal Road, Vanakla, Surat — next to Swarna Lagom. The location offers excellent connectivity to schools, hospitals, markets, and major roads across Surat.',
      order: 2,
    },
    {
      id: 'av-f3',
      question: 'When is the possession date for Swarna Avsar?',
      answer: 'Swarna Avsar is currently under construction with possession expected in December 2026.',
      order: 3,
    },
    {
      id: 'av-f4',
      question: 'What flat configurations are available in Swarna Avsar?',
      answer: 'Swarna Avsar offers 2 BHK flats with RERA carpet areas of 710–713 sq.ft and 732–742 sq.ft, along with commercial shops with RERA carpet areas ranging from 227 to 1,103 sq.ft.',
      order: 4,
    },
    {
      id: 'av-f5',
      question: 'What is the price of flats in Swarna Avsar?',
      answer: 'Pricing for Swarna Avsar is available on request. Please contact our sales team via WhatsApp (+919925026000) or phone for the latest pricing and payment plan details.',
      order: 5,
    },
    {
      id: 'av-f6',
      question: 'Is Swarna Avsar RERA registered?',
      answer: 'Yes, Swarna Avsar is registered under GujRERA (PR/GJ/SURAT/SURAT CITY/SMC/MAA14060/300824/311228). You can verify the project details on the official GujRERA portal.',
      order: 6,
    },
    {
      id: 'av-f7',
      question: 'Does Swarna Avsar include commercial shops?',
      answer: 'Yes. Swarna Avsar includes commercial shops along with premium 2 BHK residences, making it suitable for both homebuyers and business/investment buyers.',
      order: 7,
    },
  ],
  seo: {
    metaTitle: 'Swarna Avsar | 2BHK Flats in Surat | Swarna Group',
    metaDescription: 'Buy 2BHK flats at Swarna Avsar in Surat, Gujarat. Premium construction by Swarna Group on Palanpore Canal Road. Schedule a free site visit today.',
    targetKeywords: ['2 bhk flats in vanakla surat', 'flats near palanpur canal road', 'swarna avsar surat', '2 bhk in jahangirpura'],
    canonicalUrl: 'https://www.swarnagroup.in/projects/swarna-avsar',
    ogTitle: 'Swarna Avsar — Not Only Largest, Finest Too',
    ogDescription: '148 premium 2 BHK residences across 3 wings on Ugat Canal Road, Vanakla Surat.',
    schemaType: 'RealEstateListing',
  },
  isPublished: true,
  featured: true,
  createdAt: '2026-06-12T09:05:50Z',
  updatedAt: '2026-09-29T16:30:00.000Z',
  updatedBy: 'Admin',
};

export const DEFAULT_LAGOM_PROJECT: CmsProject = {
  id: 'swarna-lagom',
  slug: 'swarna-lagom',
  name: 'Swarna Lagom',
  tagline: 'Premium · Spacious · Affordable',
  description:
    "Swarna Lagom is a premium 2 BHK residential highrise in Vanakla, Surat — designed around a simple philosophy: not too little, not too much, just right. Strategically located on Ugat-Canal Road with seamless connectivity to the city's key landmarks, every apartment is thoughtfully planned to maximise natural light, ventilation, and space. With an earthquake-resistant structure, 3-level parking, full security infrastructure, and a landscaped community garden, Swarna Lagom delivers a quality of living that goes beyond the ordinary.",
  shortDescription:
    'Premium, spacious, and affordable 2 BHK highrise on Ugat-Canal Road, Vanakla — where thoughtful design meets everyday comfort.',
  location: 'Beside Anjani Enclave, Ugat-Canal Road, Vanakla, Surat — 395005',
  city: 'Surat',
  microMarket: 'Vanakla',
  status: 'ready-possession',
  completionDate: 'Ready Possession (BUC Received)',
  heroImage: '/og-default.jpg',
  thumbnailImage: '/og-default.jpg',
  totalFloors: 14,
  totalUnits: 126,
  reraNumber: 'PR/GJ/SURAT/SURAT CITY/SUDA/MAA07040/280520',
  mapEmbedUrl:
    'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3718.820245525998!2d72.77720817589176!3d21.23897548052401!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be04df57cfa804b%3A0xa0ea6fafdf5ab243!2sSwarna%20Lagom!5e0!3m2!1sen!2sin!4v1781421910895!5m2!1sen!2sin',
  whatsappNumber: '+919925026000',
  brochureUrl: '',
  configurations: [
    {
      id: 'lagom-cfg-1',
      type: 'Commercial Shops',
      area: '137 – 1,274 sq.ft (RERA Carpet)',
      price: 'Contact for pricing',
      units: 18,
      availabilityStatus: 'Available',
    },
    {
      id: 'lagom-cfg-2',
      type: '2 BHK (Type A)',
      area: '694 sq.ft (RERA Carpet)',
      price: 'Contact for pricing',
      units: 54,
      availabilityStatus: 'Limited Units',
    },
    {
      id: 'lagom-cfg-3',
      type: '2 BHK (Type B)',
      area: '717 sq.ft (RERA Carpet)',
      price: 'Contact for pricing',
      units: 54,
      availabilityStatus: 'Available',
    },
  ],
  highlights: [
    'Strategically designed for maximum natural light and cross ventilation',
    '3-level parking facility including double basement',
    'Earthquake-resistant R.C.C. frame structure as per IS Code',
    'Comprehensive security with CCTV surveillance and security cabin',
    'Handicapped-friendly campus with landscaped gardens and tree plantation',
    'Gujarat gas line, rainwater harvesting and centralized plumbing system',
  ],
  amenities: [
    { id: 'lg-a1', icon: 'PlayCircle', label: "Children's Play Area", category: 'wellness' },
    { id: 'lg-a2', icon: 'Tree', label: 'Landscaped Garden', category: 'wellness' },
    { id: 'lg-a3', icon: 'Leaf', label: 'Yoga / Meditation Deck', category: 'wellness' },
    { id: 'lg-a4', icon: 'Barbell', label: 'Fitness & Wellness Zone', category: 'wellness' },
    { id: 'lg-a5', icon: 'Buildings', label: 'Multipurpose Terrace & Clubhouse', category: 'convenience' },
    { id: 'lg-a6', icon: 'ShieldCheck', label: 'Security & CCTV Surveillance', category: 'safety' },
    { id: 'lg-a7', icon: 'Car', label: '3-Level Parking', category: 'convenience' },
  ],
  specifications: [
    {
      id: 'lg-s1',
      heading: 'Flooring',
      items: [
        { label: 'Living / Dining / Kitchen', value: '32" × 32" Vitrified tiles' },
        { label: 'Bedrooms', value: '24" × 24" Vitrified tiles' },
        { label: 'Balcony', value: 'Anti-skid outdoor tiles' },
      ],
    },
    {
      id: 'lg-s2',
      heading: 'Kitchen',
      items: [
        { label: 'Platform', value: 'Granite platform with stainless steel sink' },
        { label: 'Wall Tiles', value: 'Decorative Granamite / glazed dado tiles' },
      ],
    },
    {
      id: 'lg-s3',
      heading: 'Doors & Windows',
      items: [
        { label: 'Main Door', value: 'Decorative door with wooden frame' },
        { label: 'Internal Doors', value: 'Enamel paint flush doors with granite / wooden frames' },
        { label: 'Windows', value: 'Anodized sliding aluminum windows with granite seal' },
      ],
    },
  ],
  gallery: [],
  documents: [],
  faqs: [
    {
      id: 'lg-f1',
      question: 'What is Swarna Lagom?',
      answer: 'Swarna Lagom is a premium 2 BHK residential highrise by Swarna Group, located on Ugat Canal Road, Vanakla, Surat. Designed for modern family living, it offers spacious and affordable homes with quality construction and thoughtful layouts.',
      order: 1,
    },
    {
      id: 'lg-f2',
      question: 'What is the possession status of Swarna Lagom?',
      answer: 'Swarna Lagom is ready to move in. Possession is available immediately upon booking completion.',
      order: 2,
    },
    {
      id: 'lg-f3',
      question: 'What flat configurations are available in Swarna Lagom?',
      answer: 'Swarna Lagom offers 2 BHK flats with RERA carpet areas of 694 sq.ft and 717 sq.ft, along with commercial shops with RERA carpet areas ranging from 137 to 1,274 sq.ft.',
      order: 3,
    },
    {
      id: 'lg-f4',
      question: 'What is the price of flats in Swarna Lagom?',
      answer: 'Pricing for Swarna Lagom is available on request. Please contact our sales team via WhatsApp (+919925026000) or phone for the latest pricing and payment plan details.',
      order: 4,
    },
  ],
  seo: {
    metaTitle: 'Swarna Lagom | 2BHK Flats & Shops in Surat',
    metaDescription: 'Swarna Lagom offers 2BHK flats (694–717 sq.ft RERA carpet area) and commercial shops in Surat, Gujarat. Premium project by Swarna Group. Enquire now.',
    targetKeywords: ['ready possession flats in surat', '2 bhk flats in vanakla', 'swarna lagom surat', 'shops in vanakla surat'],
    canonicalUrl: 'https://www.swarnagroup.in/projects/swarna-lagom',
    ogTitle: 'Swarna Lagom — Premium · Spacious · Affordable',
    ogDescription: 'Ready possession 2 BHK apartments & retail commercial shops in Vanakla, Surat.',
    schemaType: 'RealEstateListing',
  },
  isPublished: true,
  featured: true,
  createdAt: '2026-06-14T05:43:48Z',
  updatedAt: '2026-09-29T16:30:00.000Z',
  updatedBy: 'Admin',
};

const LOCAL_STORAGE_CMS_KEY = 'swarna_cms_projects_cache_v5';

export class WebsiteCmsService {
  public static async getProjectDrafts(): Promise<Record<string, CmsProject>> {
    const db = getFirestore();
    const snap = await getDocs(collection(db, DRAFT_COLLECTION_NAME));
    const drafts: Record<string, CmsProject> = {};
    snap.docs.forEach((item) => { drafts[item.id] = item.data() as CmsProject; });
    return drafts;
  }

  public static async saveProjectDraft(project: CmsProject, userEmail: string): Promise<CmsProject> {
    const draft = { ...project, updatedAt: new Date().toISOString(), updatedBy: userEmail };
    const db = getFirestore();
    await setDoc(doc(db, DRAFT_COLLECTION_NAME, project.slug), draft);
    return draft;
  }

  private static getLocalCache(): CmsProject[] {
    try {
      const raw = localStorage.getItem(LOCAL_STORAGE_CMS_KEY);
      if (raw) return JSON.parse(raw);
    } catch {
      // Fallback
    }
    return [DEFAULT_AVSAR_PROJECT, DEFAULT_LAGOM_PROJECT];
  }

  private static setLocalCache(projects: CmsProject[]): void {
    try {
      localStorage.setItem(LOCAL_STORAGE_CMS_KEY, JSON.stringify(projects));
    } catch {
      // Ignore
    }
  }

  /**
   * Fetch all CMS projects from Firestore (with local fallback)
   */
  public static async getAllProjects(): Promise<CmsProject[]> {
    const db = getFirestore();
    const snap = await getDocs(collection(db, COLLECTION_NAME));
    const list = snap.docs.map((d) => ({ id: d.id, ...d.data() } as CmsProject));
    this.setLocalCache(list);
    return list;
  }

  /**
   * Save or update a project in Firestore & update SEO collection
   */
  public static async saveProject(project: CmsProject, userEmail: string = 'Admin'): Promise<CmsProject> {
    const updated: CmsProject = {
      ...project,
      updatedAt: new Date().toISOString(),
      updatedBy: userEmail,
    };

    const db = getFirestore();
    const batch = writeBatch(db);
    batch.set(doc(db, COLLECTION_NAME, project.slug), updated, { merge: true });
    if (project.seo) {
      batch.set(doc(db, SEO_COLLECTION_NAME, project.slug), {
            slug: project.slug,
            projectName: project.name,
            ...project.seo,
            faqs: project.faqs || [],
            updatedAt: updated.updatedAt,
            updatedBy: userEmail,
      }, { merge: true });
    }
    batch.delete(doc(db, DRAFT_COLLECTION_NAME, project.slug));
    await batch.commit();

    const current = this.getLocalCache();
    const idx = current.findIndex((p) => p.slug === project.slug || p.id === project.id);
    const nextList = [...current];
    if (idx >= 0) nextList[idx] = updated;
    else nextList.push(updated);
    this.setLocalCache(nextList);

    return updated;
  }

  // ─── Autonomous SEO Engine & Month 1 Review Queue ────────────────────────────────

  /**
   * Fetch current SEO engine configuration (Supervised Review vs Full Autopilot)
   */
  public static async getSeoEngineConfig(): Promise<SeoEngineConfig> {
    const defaultConfig: SeoEngineConfig = {
      mode: 'supervised_review', // Month 1 default: Supervised Review
      autoSyncEnabled: false,
      minAuditScoreThreshold: 85,
      notifyOnPendingReview: true,
      lastAutonomousRun: new Date().toISOString(),
      totalAutoOptimizations: 0,
    };

    try {
      const db = getFirestore();
      const snap = await getDoc(doc(db, SEO_CONFIG_COLLECTION, 'global'));
      if (snap.exists()) {
        return { ...defaultConfig, ...snap.data() };
      }
    } catch (err) {
      console.warn('getSeoEngineConfig fallback to default:', err);
    }
    return defaultConfig;
  }

  /**
   * Save SEO engine configuration
   */
  public static async saveSeoEngineConfig(config: SeoEngineConfig): Promise<void> {
    const db = getFirestore();
    await setDoc(doc(db, SEO_CONFIG_COLLECTION, 'global'), config, { merge: true });
  }

  /**
   * Fetch all pending SEO reviews for Month 1 Supervised Mode
   */
  public static async getPendingSeoReviews(): Promise<SeoPendingApprovalItem[]> {
    try {
      const db = getFirestore();
      const snap = await getDocs(collection(db, SEO_QUEUE_COLLECTION));
      if (!snap.empty) {
        return snap.docs
          .map((d) => ({ id: d.id, ...d.data() } as SeoPendingApprovalItem))
          .filter((item) => item.status === 'pending');
      }
    } catch (err) {
      console.warn('getPendingSeoReviews failed:', err);
    }
    return [];
  }

  /**
   * Save or queue a new pending SEO review item
   */
  public static async savePendingSeoReview(item: SeoPendingApprovalItem): Promise<void> {
    const db = getFirestore();
    await setDoc(doc(db, SEO_QUEUE_COLLECTION, item.id), item, { merge: true });
  }

  /**
   * Approve a pending SEO review item and immediately push live
   */
  public static async approveSeoReview(item: SeoPendingApprovalItem, reviewerEmail = 'Admin'): Promise<void> {
    try {
      const db = getFirestore();

      // 1. Mark item approved in queue
      await setDoc(
        doc(db, SEO_QUEUE_COLLECTION, item.id),
        {
          status: 'approved',
          reviewedAt: new Date().toISOString(),
          reviewedBy: reviewerEmail,
        },
        { merge: true }
      );

      // 2. Dispatch to live Firestore SEO document
      if (item.entityType === 'PROPERTY') {
        await this.syncPropertySeo({
          projectKey: item.targetSlug,
          projectName: item.entityName,
          seoTitle: item.proposedTitle,
          metaDescription: item.proposedDescription,
          faqs: item.proposedFaqs,
          schemaJson: item.proposedSchemaJson,
        });
      } else if (item.entityType === 'LOCALITY') {
        await this.syncLocalitySeo({
          slug: item.targetSlug,
          metaTitle: item.proposedTitle,
          metaDescription: item.proposedDescription,
          targetKeywords: item.proposedKeywords,
          faqs: item.proposedFaqs,
          schemaJson: item.proposedSchemaJson,
        });
      } else if (item.entityType === 'HOMEPAGE') {
        await this.syncHomepageSeo({
          metaTitle: item.proposedTitle,
          metaDescription: item.proposedDescription,
          targetKeywords: item.proposedKeywords,
          faqs: item.proposedFaqs,
          schemaJson: item.proposedSchemaJson,
        });
      }
    } catch (err) {
      console.warn('approveSeoReview failed:', err);
      throw err;
    }
  }

  /**
   * Batch approve all pending SEO recommendations in 1-Click
   */
  public static async approveAllSeoReviews(items: SeoPendingApprovalItem[], reviewerEmail = 'Admin'): Promise<void> {
    for (const item of items) {
      await this.approveSeoReview(item, reviewerEmail);
    }
  }

  /**
   * Reject / Dismiss a pending SEO recommendation
   */
  public static async rejectSeoReview(reviewId: string, reviewerEmail = 'Admin'): Promise<void> {
    try {
      const db = getFirestore();
      await setDoc(
        doc(db, SEO_QUEUE_COLLECTION, reviewId),
        {
          status: 'rejected',
          reviewedAt: new Date().toISOString(),
          reviewedBy: reviewerEmail,
        },
        { merge: true }
      );
    } catch (err) {
      console.warn('rejectSeoReview failed:', err);
    }
  }

  /**
   * Fetch all live SEO records from Firestore
   */
  public static async getAllLiveSeoRecords(): Promise<Record<string, any>> {
    const result: Record<string, any> = {};
    try {
      const db = getFirestore();
      const snap = await getDocs(collection(db, SEO_COLLECTION_NAME));
      if (!snap.empty) {
        snap.docs.forEach((d) => {
          result[d.id] = d.data();
        });
      }
    } catch (err) {
      console.warn('getAllLiveSeoRecords failed:', err);
    }
    return result;
  }

  /**
   * Sync Homepage SEO to Firestore
   */
  public static async syncHomepageSeo(data: any): Promise<void> {
    const db = getFirestore();
    await setDoc(
        doc(db, SEO_COLLECTION_NAME, 'homepage'),
        {
          ...data,
          updatedAt: new Date().toISOString(),
        },
        { merge: true }
      );
  }

  /**
   * Sync Locality Hub SEO to Firestore
   */
  public static async syncLocalitySeo(data: any): Promise<void> {
    const db = getFirestore();
    const slug = data.slug || data.id || 'locality';
    await setDoc(
        doc(db, SEO_COLLECTION_NAME, slug),
        {
          ...data,
          updatedAt: new Date().toISOString(),
        },
        { merge: true }
      );
  }

  /**
   * Save a full Locality Hub to Firestore and sync its SEO metadata
   */
  public static async saveLocalityHub(hub: LocalityHubSeoItem): Promise<void> {
      const db = getFirestore();
      const slug = hub.slug || hub.id;
      const batch = writeBatch(db);
      batch.set(
        doc(db, LOCALITY_HUBS_COLLECTION, slug),
        {
          ...hub,
          updatedAt: new Date().toISOString(),
        },
        { merge: true }
      );

      batch.set(
        doc(db, SEO_COLLECTION_NAME, slug),
        {
          slug,
          title: hub.title,
          metaTitle: hub.seoTitle,
          metaDescription: hub.metaDescription,
          richGuideHtml: hub.richGuideHtml,
          faqs: hub.faqs || [],
          schemaJson: hub.schemaJson || {},
          updatedAt: new Date().toISOString(),
        },
        { merge: true }
      );
      await batch.commit();
  }

  /**
   * Fetch all Locality Hubs from Firestore
   */
  public static async getAllFirestoreLocalityHubs(): Promise<LocalityHubSeoItem[]> {
    try {
      const db = getFirestore();
      const snap = await getDocs(collection(db, LOCALITY_HUBS_COLLECTION));
      if (!snap.empty) {
        return snap.docs.map((d) => ({ id: d.id, ...d.data() } as LocalityHubSeoItem));
      }
    } catch (err) {
      console.warn('getAllFirestoreLocalityHubs failed:', err);
    }
    return [];
  }

  /**
   * Delete a Locality Hub from Firestore
   */
  public static async deleteLocalityHub(slug: string): Promise<void> {
    const db = getFirestore();
    const batch = writeBatch(db);
    batch.delete(doc(db, LOCALITY_HUBS_COLLECTION, slug));
    batch.delete(doc(db, SEO_COLLECTION_NAME, slug));
    await batch.commit();
  }

  /**
   * Sync Property SEO to both website_seo and website_projects
   */
  public static async syncPropertySeo(data: any): Promise<void> {
    const db = getFirestore();
    const projectKey = data.projectKey || 'swarna-avsar';
    const batch = writeBatch(db);
      
      // Update SEO doc
      batch.set(
        doc(db, SEO_COLLECTION_NAME, projectKey),
        {
          slug: projectKey,
          projectName: data.projectName,
          metaTitle: data.seoTitle,
          metaDescription: data.metaDescription,
          faqs: data.faqs || [],
          schemaJson: data.schemaJson || {},
          updatedAt: new Date().toISOString(),
        },
        { merge: true }
      );

      // Also update project doc's SEO and FAQs
      batch.set(
        doc(db, COLLECTION_NAME, projectKey),
        {
          seo: {
            metaTitle: data.seoTitle,
            metaDescription: data.metaDescription,
            keywords: [],
          },
          faqs: (data.faqs || []).map((f: any, idx: number) => ({
            id: `faq-${Date.now()}-${idx}`,
            question: f.question,
            answer: f.answer,
          })),
          updatedAt: new Date().toISOString(),
        },
        { merge: true }
      );
    await batch.commit();
  }

  /**
   * Delete a project
   */
  public static async deleteProject(slug: string): Promise<void> {
    const db = getFirestore();
    const batch = writeBatch(db);
    batch.delete(doc(db, COLLECTION_NAME, slug));
    batch.delete(doc(db, SEO_COLLECTION_NAME, slug));
    batch.delete(doc(db, DRAFT_COLLECTION_NAME, slug));
    await batch.commit();
    this.setLocalCache(this.getLocalCache().filter((p) => p.slug !== slug));
  }

  /**
   * Upload file to Firebase Storage with automatic in-browser compression for images
   * and real-time progress callbacks for heavy videos.
   */
  public static async uploadMedia(
    file: File,
    projectSlug: string,
    folder: string = 'gallery',
    onProgress?: (percent: number) => void
  ): Promise<string> {
    try {
      // 1. Automatically compress images before uploading
      let uploadFile = file;
      if (file.type.startsWith('image/')) {
        const isHero = folder === 'hero' || file.name.includes('hero');
        uploadFile = await compressImage(file, {
          maxWidth: isHero ? 1920 : 1400,
          quality: 0.82,
        });
      }

      const storage = getStorage();
      const ext = uploadFile.name.split('.').pop() || 'jpg';
      const cleanName = `${Date.now()}_${Math.random().toString(36).substring(2, 7)}.${ext}`;
      const storageRef = ref(storage, `website/${projectSlug}/${folder}/${cleanName}`);

      // If progress callback provided, use resumable upload
      if (onProgress) {
        return new Promise((resolve, reject) => {
          const uploadTask = uploadBytesResumable(storageRef, uploadFile);
          uploadTask.on(
            'state_changed',
            (snapshot) => {
              const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
              onProgress(progress);
            },
            (error) => {
              console.warn('Upload error:', error);
              reject(error);
            },
            async () => {
              const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);
              resolve(downloadUrl);
            }
          );
        });
      }
      
      const snapshot = await uploadBytes(storageRef, uploadFile);
      const url = await getDownloadURL(snapshot.ref);
      return url;
    } catch (err) {
      console.error('Firebase Storage upload failed', err);
      throw err;
    }
  }

  // ==========================================
  // TESTIMONIALS (Customer Reviews & Videos)
  // ==========================================
  public static async getAllTestimonials(): Promise<CmsTestimonial[]> {
    try {
      const db = getFirestore();
      const snap = await getDocs(collection(db, TESTIMONIALS_COLLECTION));
      if (!snap.empty) {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() } as CmsTestimonial));
        localStorage.setItem('swarna_cms_testimonials_v1', JSON.stringify(list));
        return list;
      }
    } catch (err) {
      console.warn('Firestore fetch testimonials failed, using local cache', err);
    }
    try {
      const raw = localStorage.getItem('swarna_cms_testimonials_v1');
      if (raw) return JSON.parse(raw);
    } catch {}
    return DEFAULT_TESTIMONIALS;
  }

  public static async saveTestimonial(item: CmsTestimonial): Promise<CmsTestimonial> {
    const db = getFirestore();
    await setDoc(doc(db, TESTIMONIALS_COLLECTION, item.id), item, { merge: true });
    const list = (await this.getAllTestimonials()).filter((t) => t.id !== item.id);
    const updatedList = [item, ...list];
    localStorage.setItem('swarna_cms_testimonials_v1', JSON.stringify(updatedList));

    return item;
  }

  public static async deleteTestimonial(id: string): Promise<void> {
    const db = getFirestore();
    await deleteDoc(doc(db, TESTIMONIALS_COLLECTION, id));
    const list = (await this.getAllTestimonials()).filter((t) => t.id !== id);
    localStorage.setItem('swarna_cms_testimonials_v1', JSON.stringify(list));
  }

  // ==========================================
  // INFLUENCER VIDEOS & WALKTHROUGHS
  // ==========================================
  public static async getAllInfluencerVideos(): Promise<CmsInfluencerVideo[]> {
    try {
      const db = getFirestore();
      const snap = await getDocs(collection(db, INFLUENCER_COLLECTION));
      if (!snap.empty) {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() } as CmsInfluencerVideo));
        localStorage.setItem('swarna_cms_influencers_v1', JSON.stringify(list));
        return list;
      }
    } catch (err) {
      console.warn('Firestore fetch influencer videos failed, using local cache', err);
    }
    try {
      const raw = localStorage.getItem('swarna_cms_influencers_v1');
      if (raw) return JSON.parse(raw);
    } catch {}
    return DEFAULT_INFLUENCER_VIDEOS;
  }

  public static async saveInfluencerVideo(item: CmsInfluencerVideo): Promise<CmsInfluencerVideo> {
    const db = getFirestore();
    await setDoc(doc(db, INFLUENCER_COLLECTION, item.id), item, { merge: true });
    const list = (await this.getAllInfluencerVideos()).filter((v) => v.id !== item.id);
    const updatedList = [item, ...list];
    localStorage.setItem('swarna_cms_influencers_v1', JSON.stringify(updatedList));

    return item;
  }

  public static async deleteInfluencerVideo(id: string): Promise<void> {
    const db = getFirestore();
    await deleteDoc(doc(db, INFLUENCER_COLLECTION, id));
    const list = (await this.getAllInfluencerVideos()).filter((v) => v.id !== id);
    localStorage.setItem('swarna_cms_influencers_v1', JSON.stringify(list));
  }

  // ==========================================
  // PAST PROJECTS & HERITAGE MILESTONES
  // ==========================================
  public static async getAllPastProjects(): Promise<CmsPastProject[]> {
    try {
      const db = getFirestore();
      const snap = await getDocs(collection(db, PAST_PROJECTS_COLLECTION));
      if (!snap.empty) {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() } as CmsPastProject));
        localStorage.setItem('swarna_cms_past_projects_v1', JSON.stringify(list));
        return list;
      }
    } catch (err) {
      console.warn('Firestore fetch past projects failed, using local cache', err);
    }
    try {
      const raw = localStorage.getItem('swarna_cms_past_projects_v1');
      if (raw) return JSON.parse(raw);
    } catch {}
    return DEFAULT_PAST_PROJECTS;
  }

  public static async savePastProject(item: CmsPastProject): Promise<CmsPastProject> {
    const db = getFirestore();
    await setDoc(doc(db, PAST_PROJECTS_COLLECTION, item.id), item, { merge: true });
    const list = (await this.getAllPastProjects()).filter((p) => p.id !== item.id);
    const updatedList = [item, ...list];
    localStorage.setItem('swarna_cms_past_projects_v1', JSON.stringify(updatedList));

    return item;
  }

  public static async deletePastProject(id: string): Promise<void> {
    const db = getFirestore();
    await deleteDoc(doc(db, PAST_PROJECTS_COLLECTION, id));
    const list = (await this.getAllPastProjects()).filter((p) => p.id !== id);
    localStorage.setItem('swarna_cms_past_projects_v1', JSON.stringify(list));
  }

  // ==========================================
  // SITE SETTINGS & BRAND DATA (Singleton)
  // ==========================================
  public static async getSiteSettings(): Promise<CmsSiteSettings> {
    try {
      const db = getFirestore();
      const snap = await getDoc(doc(db, SETTINGS_COLLECTION, 'global'));
      if (snap.exists()) {
        const data = snap.data() as CmsSiteSettings;
        localStorage.setItem('swarna_cms_settings_v1', JSON.stringify(data));
        return data;
      }
    } catch (err) {
      console.warn('Firestore fetch site settings failed, using local cache', err);
    }
    try {
      const raw = localStorage.getItem('swarna_cms_settings_v1');
      if (raw) return JSON.parse(raw);
    } catch {}
    return DEFAULT_SITE_SETTINGS;
  }

  public static async saveSiteSettings(settings: CmsSiteSettings): Promise<CmsSiteSettings> {
    const updated: CmsSiteSettings = {
      ...settings,
      updatedAt: new Date().toISOString(),
    };
    const db = getFirestore();
    const batch = writeBatch(db);
    batch.set(doc(db, SETTINGS_COLLECTION, 'global'), updated, { merge: true });
    batch.set(doc(db, SEO_COLLECTION_NAME, 'homepage'), {
      metaTitle: updated.seoTitle,
      metaDescription: updated.seoDescription,
      updatedAt: updated.updatedAt,
    }, { merge: true });
    await batch.commit();
    localStorage.setItem('swarna_cms_settings_v1', JSON.stringify(updated));
    return updated;
  }
}

export const DEFAULT_TESTIMONIALS: CmsTestimonial[] = [
  {
    id: 'test-1',
    customerName: 'Laxmanbhai Prajapati',
    designation: 'Homeowner, Swarna Avsar',
    projectName: 'Swarna Avsar',
    projectSlug: 'swarna-avsar',
    videoUrl: '',
    quote: 'ઘર જોયું અને મનમાં ક્લિક થઈ ગયું — આ જ ઘર છે.',
    featured: true,
    order: 0,
  },
  {
    id: 'test-2',
    customerName: 'Ravi Prajapati',
    designation: 'Homeowner, Swarna Lagom',
    projectName: 'Swarna Lagom',
    projectSlug: 'swarna-lagom',
    videoUrl: '',
    quote: 'The build quality, the parking, the ventilation — Swarna Lagom delivered on every promise.',
    featured: true,
    order: 1,
  },
  {
    id: 'test-3',
    customerName: 'Narendrabhai Rawal',
    designation: 'Homeowner, Swarna Lagom',
    projectName: 'Swarna Lagom',
    projectSlug: 'swarna-lagom',
    videoUrl: '',
    quote: 'ક્વૉલિટી, પાર્કિંગ, સિક્યૉરિટી — સ્વર્ણ લેગોમે બધું ધાર્યા કરતાં સારું આપ્યું.',
    featured: true,
    order: 2,
  },
  {
    id: 'test-4',
    customerName: 'Jay Charkhawala',
    designation: 'Homeowner, Swarna Lagom',
    projectName: 'Swarna Lagom',
    projectSlug: 'swarna-lagom',
    videoUrl: '',
    quote: 'સ્વર્ણા ગ્રુપ પર ભરોસો કર્યો અને એ નિર્ણય સાચો સાબિત થયો.',
    featured: true,
    order: 3,
  },
];

export const DEFAULT_INFLUENCER_VIDEOS: CmsInfluencerVideo[] = [
  {
    id: 'inf-1',
    title: 'Sample Tour - Swarna Avsar',
    videoUrl: '',
    projectSlug: 'swarna-avsar',
    featured: true,
    order: 0,
  },
  {
    id: 'inf-2',
    title: 'Influencer - 1 Walkthrough',
    videoUrl: '',
    projectSlug: 'swarna-avsar',
    featured: true,
    order: 1,
  },
];

export const DEFAULT_PAST_PROJECTS: CmsPastProject[] = [
  {
    id: 'past-1',
    entryType: 'event',
    name: 'Shanti Group Founded',
    yearRange: '1988',
    sortYear: 1988,
    description: 'Shanti Group was founded in Surat in 1989 — beginning a journey of building homes, trust, and community that continues to this day.',
  },
  {
    id: 'past-2',
    entryType: 'event',
    name: 'Swarna Group Established',
    yearRange: '2020',
    sortYear: 2020,
    description: 'As the next generation stepped into Shanti Group, a natural demerger in 2020 led to the formation of Swarna Group — a new chapter built on the same foundation of trust, quality, and a deep-rooted commitment to the families of Surat.',
  },
  {
    id: 'past-3',
    entryType: 'project',
    name: 'Shanti Kunj',
    yearRange: '1989-90',
    sortYear: 1989,
    projectType: 'Residential Apartments',
    location: 'Adajan, Surat',
  },
  {
    id: 'past-4',
    entryType: 'project',
    name: 'Shanti Van',
    yearRange: '1990-91',
    sortYear: 1990,
  },
  {
    id: 'past-5',
    entryType: 'project',
    name: 'Shanti Nath',
    yearRange: '1990-92',
    sortYear: 1990,
    projectType: 'Residential Apartments',
    location: 'Adajan, Surat',
    totalUnits: 100,
    superBuiltUpArea: 1000,
  },
  {
    id: 'past-6',
    entryType: 'project',
    name: 'Shanti Vihar',
    yearRange: '1991-93',
    sortYear: 1991,
    projectType: 'Row Houses',
    location: 'Palanpore, Surat',
    totalUnits: 150,
    superBuiltUpArea: 1200,
  },
  {
    id: 'past-7',
    entryType: 'project',
    name: 'Samarth Appartment',
    yearRange: '1991-92',
    sortYear: 1991,
    projectType: 'Residential Apartments',
    location: 'Adajan, Surat',
    totalUnits: 16,
    superBuiltUpArea: 1000,
  },
  {
    id: 'past-8',
    entryType: 'project',
    name: 'Shanti Villa',
    yearRange: '1993-94',
    sortYear: 1993,
    projectType: 'Mixed Use',
    location: 'Adajan, Surat',
    totalUnits: 100,
    superBuiltUpArea: 1000,
  },
  {
    id: 'past-9',
    entryType: 'project',
    name: 'Shanti Bhavan',
    yearRange: '1993-95',
    sortYear: 1993,
    projectType: 'Residential Apartments',
    location: 'Adajan, Surat',
    totalUnits: 140,
    superBuiltUpArea: 900,
  },
  {
    id: 'past-10',
    entryType: 'project',
    name: 'Shanti Complex',
    yearRange: '1995-97',
    sortYear: 1995,
    projectType: 'Residential Apartments',
    location: 'Rander, Surat',
    totalUnits: 50,
    superBuiltUpArea: 800,
  },
  {
    id: 'past-11',
    entryType: 'project',
    name: 'Smet Shikhar',
    yearRange: '1996-97',
    sortYear: 1996,
    projectType: 'Residential Apartments',
    location: 'Adajan, Surat',
    totalUnits: 40,
    superBuiltUpArea: 1400,
  },
  {
    id: 'past-12',
    entryType: 'project',
    name: 'Shanti Niketan',
    yearRange: '1997-99',
    sortYear: 1997,
    projectType: 'Row Houses',
    location: 'Adajan, Surat',
    totalUnits: 117,
    superBuiltUpArea: 1300,
  },
  {
    id: 'past-13',
    entryType: 'project',
    name: 'Shanti Sadan',
    yearRange: '1998-00',
    sortYear: 1998,
    projectType: 'Mixed Use',
    location: 'Palanpore, Surat',
  },
  {
    id: 'past-14',
    entryType: 'project',
    name: 'Saint Park',
    yearRange: '1998-02',
    sortYear: 1998,
    projectType: 'Residential Apartments',
    location: 'Adajan, Surat',
    totalUnits: 220,
    superBuiltUpArea: 1100,
  },
  {
    id: 'past-15',
    entryType: 'project',
    name: 'Shanti Palace',
    yearRange: '1999-00',
    sortYear: 1999,
    projectType: 'Bungalows',
    location: 'Rander, Surat',
    totalUnits: 53,
    superBuiltUpArea: 2500,
  },
  {
    id: 'past-16',
    entryType: 'project',
    name: 'Shanti Sagar',
    yearRange: '2001-03',
    sortYear: 2001,
    projectType: 'Row Houses',
    location: 'Pal, Surat',
    totalUnits: 220,
    superBuiltUpArea: 1200,
  },
  {
    id: 'past-17',
    entryType: 'project',
    name: 'Shanti Deep',
    yearRange: '2005-08',
    sortYear: 2005,
    projectType: 'Residential Apartments',
    location: 'Pal, Surat',
    totalUnits: 124,
    superBuiltUpArea: 1500,
  },
  {
    id: 'past-18',
    entryType: 'project',
    name: 'Swapna Shrushti',
    yearRange: '2007-15',
    sortYear: 2007,
    projectType: 'Residential Apartments',
    location: 'Bhestan, Surat',
    totalUnits: 1040,
    superBuiltUpArea: 850,
  },
  {
    id: 'past-19',
    entryType: 'project',
    name: 'Prabhu Darshan',
    yearRange: '2005-12',
    sortYear: 2005,
    projectType: 'Row Houses',
    location: 'Jahangirabad, Surat',
    totalUnits: 108,
    superBuiltUpArea: 1300,
  },
  {
    id: 'past-20',
    entryType: 'project',
    name: 'Salaj Homes',
    yearRange: '2010-12',
    sortYear: 2010,
    projectType: 'Residential Apartments',
    location: 'Jahangirabad, Surat',
    totalUnits: 160,
    superBuiltUpArea: 1300,
    coverImageUrl: '/og-default.jpg',
  },
  {
    id: 'past-21',
    entryType: 'project',
    name: 'Shashank Row House',
    yearRange: '2012-13',
    sortYear: 2012,
    projectType: 'Row Houses',
    location: 'Vanakla, Surat',
    totalUnits: 4,
    superBuiltUpArea: 2200,
  },
  {
    id: 'past-22',
    entryType: 'project',
    name: 'Corona Heights',
    yearRange: '2014-16',
    sortYear: 2014,
    projectType: 'Residential Apartments',
    location: 'Pal, Surat',
    totalUnits: 22,
    superBuiltUpArea: 4700,
  },
  {
    id: 'past-23',
    entryType: 'project',
    name: 'Rajvi Bunglow',
    yearRange: '2015-16',
    sortYear: 2015,
    projectType: 'Row Houses',
    location: 'Jahangirpura, Surat',
    totalUnits: 18,
    superBuiltUpArea: 2800,
  },
];

export const DEFAULT_SITE_SETTINGS: CmsSiteSettings = {
  siteLogo: '/logo.png',
  favicon: '/favicon.ico',
  seoTitle: 'Swarna Group — Premium 2BHK Flats in Surat',
  seoDescription: 'Trusted real estate builder in Surat since 1989. Buy premium 2BHK flats on Palanpore Canal Road. Quality construction, on-time delivery.',
  ogImage: '/og-default.jpg',
  heroImage: '/og-default.jpg',
  heroImages: [
    '/og-default.jpg',
  ],
  aboutImage1: '/og-default.jpg',
  aboutImage2: '/og-default.jpg',
  officeMapUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3718.8162547503093!2d72.77725997589171!3d21.23913368051851!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m2!1s0x3be04d0047257c65%3A0xc813e0f91bb97dac!2sSwarna%20Avsar!5e0!3m2!1sen!2sin!4v1781331941690!5m2!1sen!2sin',
  faqs: [
    {
      question: 'Who is Swarna Group?',
      answer: "Swarna Group is a trusted real estate developer based in Surat, Gujarat, building quality residential projects since 1989. With over 35 years of experience and 2,100+ families housed, we are one of Surat's most established builders focused on premium 2BHK flats.",
    },
    {
      question: 'Where are Swarna Group projects located?',
      answer: 'Our projects are located across prime residential areas in Surat, including Palanpore Canal Road and Vanakla. These locations offer excellent connectivity, green surroundings, and proximity to schools, hospitals, and daily conveniences.',
    },
    {
      question: 'What types of flats does Swarna Group offer?',
      answer: 'We currently offer premium 2BHK flats designed for modern family living. Our homes feature quality construction, thoughtful floor plans, and amenities suited to comfortable urban life in Surat.',
    },
    {
      question: 'What is the price range of flats at Swarna Group?',
      answer: 'Flat prices vary by project, configuration, and floor. To get the latest pricing for our current projects, please contact us directly or visit our project pages on this website. Our team is happy to share detailed pricing and payment plans.',
    },
    {
      question: 'Are Swarna Group projects RERA registered?',
      answer: 'Yes, our projects are registered under the Real Estate (Regulation and Development) Act, RERA. RERA registration ensures transparency, timely delivery, and legal protection for all homebuyers.',
    },
    {
      question: 'How do I book a flat with Swarna Group?',
      answer: 'You can book a flat by contacting us via WhatsApp or phone, or by filling the enquiry form on our website. Our sales team will schedule a site visit, walk you through the project, and guide you through the booking process.',
    },
    {
      question: 'Can I visit the Swarna Group site or office?',
      answer: 'Yes, we welcome site visits. You can reach us at our Surat sales office or request a guided project visit by contacting us through the website. We are available Monday to Saturday during business hours.',
    },
    {
      question: 'How many projects has Swarna Group completed?',
      answer: 'Swarna Group has delivered multiple residential projects across Surat since 1989, housing over 2,100 families. Our heritage page lists our completed projects and milestones over the decades.',
    },
    {
      question: 'What amenities do Swarna Group residential projects include?',
      answer: "Our projects include amenities such as covered parking, landscaped open spaces, children's play areas, and quality common area finishes. Specific amenities vary by project — check individual project pages for the full list.",
    },
    {
      question: 'Is Swarna Group a reliable builder in Surat?',
      answer: 'Swarna Group has been building homes in Surat since 1989 — over 35 years of consistent delivery. With 2,100+ happy families and multiple completed projects, we have built a reputation for quality construction and trustworthy dealings across generations of Surat homebuyers.',
    },
  ],
  stats: {
    foundedYear: 1989,
    yearsActive: 35,
    totalProjects: 23,
    activeProjects: 2,
    totalFamilies: 2100,
    totalSqft: 1800000,
    localities: 6,
  },
  milestones: [
    {
      year: 1989,
      title: 'Shanti Group Founded',
      description: 'Beginning a journey of building homes, trust, and community in Surat.',
    },
    {
      year: 2020,
      title: 'Swarna Group Established',
      description: 'Demerger leading to modern high-rises and state-of-the-art living spaces in Surat.',
    },
  ],
};
